import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, concat, concatMap, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { IntegrationLookup } from '../../models/integration-lookup.model';
import { IntegrationManagerService } from '../../services/integration-manager.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { IntegrationManagev2Actions } from './integration-managev2-config.actions';
import { IntegrationManagev2InitialState } from './integration-managev2-config.reducer';
import {
    ClientIntegrationIdentifier,
    ClientIntegrationVersionIdentifier
} from '../../models/client-integration-identifier';
import { IntegrationArtifact } from '../../models/integration-artifact';
import { Store } from '@ngrx/store';
import { IntegrationManagev2Selectors } from './integration-managev2-config.selectors';
import { IntegrationVersionedConfigFile } from '../../models/integration-config-file.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import {
    PublishedVersionResponse,
    SetPublishedVersionRequest,
    SetPublishedVersionResponse
} from '../../models/integration/published-version.model';

@Injectable()
export class IntegrationManagev2Effects {
    getIntegrationLookup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.getIntegrationLookup),
            switchMap(() => this.getIntegrationLookup()),
            map((integrationLookup) => IntegrationManagev2Actions.setIntegrationLookup({ integrationLookup }))
        );
    });

    downloadIntegrationArtifact$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(IntegrationManagev2Actions.downloadConfigFile),
                switchMap((action) =>
                    this.downloadIntegrationArtifact(action.artifact).pipe(
                        tap((file) => {
                            file && this.managerService.downloadFile(file, action.artifact);
                        })
                    )
                )
            );
        },
        { dispatch: false }
    );

    getVersionsOnFirstLoad$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.getVersions),
            concatLatestFrom(() => this.featureManager.isEnabled(FeatureFlag.EnableIntegrationManageV2AddVersions)),
            switchMap(([action, isVersioningEnabled]) =>
                forkJoin({
                    versions: isVersioningEnabled ? this.getVersions(action.identifier) : of([] as string[]),
                    publishedFilenames: isVersioningEnabled
                        ? this.managerService.getIntegrationPublishedFilenames(action.identifier)
                        : this.managerService
                              .getIntegrationVersionedFiles({
                                  clientName: action.identifier.clientName,
                                  integrationType: action.identifier.integrationType,
                                  version: ''
                              })
                              .pipe(
                                  map((files) => ({
                                      filenames: files,
                                      isFromPublished: false
                                  }))
                              )
                })
            ),
            concatMap(({ versions, publishedFilenames }) =>
                concat(
                    of(
                        IntegrationManagev2Actions.setVersions({
                            versions,
                            selectedVersion: publishedFilenames.isFromPublished ? 'published' : ''
                        })
                    ),
                    of(IntegrationManagev2Actions.setConfigFiles({ files: publishedFilenames.filenames }))
                )
            )
        );
    });

    getVersionsOnCreate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.getVersionsOnCreate),
            switchMap((action) => {
                return this.getVersions(action.identifier).pipe(
                    map((versions) => {
                        return { versions, action };
                    })
                );
            }),
            concatMap(({ versions, action }) =>
                concat(
                    of(
                        IntegrationManagev2Actions.getConfigFiles({
                            identifier: {
                                ...action.identifier,
                                version: versions && versions.length > 0 ? action.identifier.version : ''
                            }
                        })
                    ),
                    of(IntegrationManagev2Actions.setVersions({ versions, selectedVersion: action.identifier.version }))
                )
            )
        );
    });

    getVersionedConfigFiles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.getConfigFiles),
            switchMap((action) =>
                this.managerService.getIntegrationVersionedFiles({
                    clientName: action.identifier.clientName,
                    integrationType: action.identifier.integrationType,
                    version: action.identifier.version
                })
            ),
            map((files) => IntegrationManagev2Actions.setConfigFiles({ files }))
        );
    });

    getVersionedConfigFileContent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.getFileContent),
            switchMap((action) => this.getversionedFileContent(action.config)),
            map((content) => {
                return IntegrationManagev2Actions.setFileContent({ content });
            })
        );
    });

    saveVersionedConfigFile$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.saveConfigFile),
            switchMap((action) =>
                this.saveConfigFile(action.config).pipe(
                    concatMap((isSaved) => {
                        return isSaved
                            ? concat(
                                  of(showToasterMessage({ message: 'File was saved successfully' })),
                                  of(
                                      IntegrationManagev2Actions.getConfigFiles({
                                          identifier: {
                                              clientName: action.config.clientName,
                                              version: action.config.version,
                                              integrationType: action.config.integrationType
                                          }
                                      })
                                  )
                              )
                            : this.showError('Error saving the file');
                    })
                )
            )
        );
    });

    deleteVersionedConfigFile$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.deleteConfigFile),
            switchMap((action) =>
                this.deleteConfigFile(action.config).pipe(
                    concatMap((isSaved) => {
                        return isSaved
                            ? concat(
                                  of(
                                      showToasterMessage({
                                          message: 'File was deleted. Deleted blobs will be retained for 60 days'
                                      })
                                  ),
                                  of(
                                      IntegrationManagev2Actions.getConfigFiles({
                                          identifier: {
                                              clientName: action.config.clientName,
                                              version: action.config.version,
                                              integrationType: action.config.integrationType
                                          }
                                      })
                                  )
                              )
                            : this.showError('Error deleting the file');
                    })
                )
            )
        );
    });

    addConfigFile$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.addConfigFile),
            switchMap((action) =>
                this.saveConfigFile(action.config).pipe(
                    concatMap((isSaved) => {
                        return isSaved
                            ? concat(
                                  of(showToasterMessage({ message: 'File was uploaded successfully' })),
                                  of(
                                      IntegrationManagev2Actions.getConfigFiles({
                                          identifier: {
                                              clientName: action.config.clientName,
                                              version: action.config.version,
                                              integrationType: action.config.integrationType
                                          }
                                      })
                                  )
                              )
                            : this.showError('Error uploading the file');
                    })
                )
            )
        );
    });

    newIntegration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.newIntegration),
            concatLatestFrom(() => this.store.select(IntegrationManagev2Selectors.selectIntegrationManagev2Lookups)),
            switchMap(([action, lookups]) =>
                this.newIntegration(action.config, lookups).pipe(
                    concatMap((response) => {
                        return response.result
                            ? concat(
                                  of(showToasterMessage({ message: response.message })),
                                  of(IntegrationManagev2Actions.getIntegrationLookup())
                              )
                            : this.showError(response.message);
                    })
                )
            )
        );
    });

    createVersion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.createVersion),
            switchMap((action) =>
                this.managerService.createVersion(action.identifier).pipe(
                    concatMap((response) => {
                        const hasError = response.errorMessage && response.errorMessage?.length > 0;
                        return hasError
                            ? this.showError(response.errorMessage)
                            : of(IntegrationManagev2Actions.getVersionsOnCreate({ identifier: action.identifier }));
                    })
                )
            )
        );
    });

    setPublishedVersion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.setPublishedVersion),
            switchMap((action) =>
                this.setPublishedVersion(action.request).pipe(
                    concatMap((response) => {
                        const message = response.isSuccess
                            ? 'Published version set successfully'
                            : `Error setting published version: ${response.errorMessage}`;
                        return concat(
                            of(showToasterMessage({ message })),
                            of(
                                IntegrationManagev2Actions.setIsSaving({
                                    isSaving: false
                                })
                            )
                        );
                    })
                )
            )
        );
    });

    getAllPublishedVersions$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(IntegrationManagev2Actions.getAllPublishedVersions),
            switchMap((action) =>
                this.getPublishedVersions(action.identifier).pipe(
                    map((publishedVersions) => {
                        return IntegrationManagev2Actions.setAllPublishedVersions({
                            publishedVersions: {
                                identifier: action.identifier,
                                publishedVersions
                            }
                        });
                    })
                )
            )
        );
    });

    private readonly managerService = inject(IntegrationManagerService);
    private store = inject(Store);
    private featureManager = inject(LDFeatureManager);

    constructor(private readonly actions$: Actions) {}

    getIntegrationLookup = (): Observable<IntegrationLookup> =>
        this.managerService
            .getIntegrationMappingsLookup()
            .pipe(catchError(() => of(IntegrationManagev2InitialState.integrationLookup)));

    getversionedFileContent = (config: IntegrationVersionedConfigFile): Observable<string> =>
        this.managerService
            .getConfigVersionedFileContent(config)
            .pipe(catchError(() => of(IntegrationManagev2InitialState.fileContent)));

    saveConfigFile = (config: IntegrationVersionedConfigFile): Observable<boolean> =>
        this.managerService.saveVersionedConfigFile(config).pipe(catchError(() => of(false)));

    deleteConfigFile = (config: IntegrationVersionedConfigFile): Observable<boolean> =>
        this.managerService.deleteVersionedConfigFile(config).pipe(catchError(() => of(false)));

    showError = (message: string) =>
        concat(
            of(
                showToasterMessage({
                    message
                })
            ),
            of(
                IntegrationManagev2Actions.setIsSaving({
                    isSaving: false
                })
            )
        );

    newIntegration = (
        config: IntegrationVersionedConfigFile,
        lookups: IntegrationLookup
    ): Observable<{ result: boolean; message: string }> => {
        const types = lookups?.clientIntegrationType[config.clientName];
        const integrationExists = types?.length > 0 && types.includes(config.integrationType);
        return integrationExists
            ? of({ result: false, message: 'Error: Integration already exists' })
            : this.managerService.saveVersionedConfigFile(config).pipe(
                  map((result) => ({ result, message: 'Integration created successfully' })),
                  catchError(() => of({ result: false, message: 'Error creating integration' }))
              );
    };

    downloadIntegrationArtifact = (artifact: IntegrationArtifact): Observable<any> =>
        this.managerService.downloadIntegrationArtifact(artifact).pipe(catchError(() => of(null)));

    getVersions = (identifier: ClientIntegrationVersionIdentifier): Observable<string[]> =>
        this.managerService
            .getIntegrationVersions(identifier)
            .pipe(catchError(() => of(IntegrationManagev2InitialState.versions)));

    setPublishedVersion = (request: SetPublishedVersionRequest): Observable<SetPublishedVersionResponse> =>
        this.managerService.setPublishedVersion(request).pipe(
            catchError(() =>
                of({
                    isSuccess: false,
                    errorMessage: 'Error setting published version'
                })
            )
        );

    getPublishedVersions = (identifier: ClientIntegrationIdentifier): Observable<PublishedVersionResponse[]> =>
        this.managerService
            .getAllPublishedVersions(identifier)
            .pipe(catchError(() => of(IntegrationManagev2InitialState.publishedVersions.publishedVersions)));
}
